/* ============================================================
   RevenueCat Web Billing — the ONLY file in the desktop client that imports
   the SDK. The browser's counterpart of app/src/billing/purchases.ts, and the
   same seam: everything above speaks `Plan` / `canWrite` from
   @habishine/shared, and the vendor is an implementation detail of how a
   `Plan` gets set.

   What is the same as on iOS:
     • the customer IS the Cognito `sub`, so a web purchase lands on the same
       RevenueCat customer, the same `pro` entitlement, the same webhook and
       the same `users.plan` the phone reads (docs/subscriptions.md §7, §10);
     • ⚠️ the client NEVER decides its own entitlement. `CustomerInfo` read
       here is an optimistic hint after a purchase and the grant-only backstop
       `EntitlementContext` runs when the server says "unentitled" — never the
       source of truth.

   What is different:
     • there is nothing to "restore": a web subscription has no receipt on the
       device, it is bound to the account that bought it. A subscriber who
       signs in on another browser is simply entitled, because the server says
       so. The one repair this exposes is "ask the server to look again".
     • the SDK renders its own checkout: `purchase()` mounts Stripe's payment
       sheet in a modal it appends to <body>, so the paywall owns the plan
       picker and nothing else.
     • managing or cancelling happens in RevenueCat's hosted customer portal,
       reached by `managementURL` (and from every receipt email), not an iOS
       sheet.
   ============================================================ */
import type { BillingAdapter } from '@habishine/client-core';
import {
  ErrorCode,
  type Package,
  type Period,
  type PricingPhase,
  Purchases,
  PurchasesError,
} from '@revenuecat/purchases-js';

import { REVENUECAT_WEB_KEY } from '../config';

/** Must match the RevenueCat dashboard and the backend's
    `REVENUECAT_ENTITLEMENT_ID` (handlers/billing-webhook.ts). */
export const ENTITLEMENT_ID = 'pro';

/** Whether this build can sell at all. False with no key: the paywall then
    points at the iOS app, exactly as it did before web billing existed. */
export const billingAvailable: boolean = REVENUECAT_WEB_KEY !== '';

/** A sandbox key talks to RevenueCat's test environment: Stripe test cards
    work and nothing is charged. Shown as a badge so "it didn't charge me" is
    diagnosable rather than mysterious. */
export const billingIsSandbox: boolean = REVENUECAT_WEB_KEY.startsWith('rcb_sb_');

/** The SDK instance, once `identify` has configured it. One per page load:
    the SDK warns about configuring twice, and `changeUser` is how it follows a
    sign-out → sign-in as somebody else. */
let instance: Purchases | null = null;

/** Who the SDK is identified as right now. */
function currentUser(): string | null {
  try {
    return instance?.getAppUserId() ?? null;
  } catch {
    return null;
  }
}

async function identify(sub: string): Promise<void> {
  if (!billingAvailable) return;
  try {
    if (!instance) {
      instance = Purchases.configure({ apiKey: REVENUECAT_WEB_KEY, appUserId: sub });
    } else if (currentUser() !== sub) {
      await instance.changeUser(sub);
    }
  } catch (err) {
    // Billing sits BESIDE the auth path (adapters.ts): a vendor script that
    // fails to boot must not take sign-in down with it. The paywall reports
    // "plans couldn't be loaded" instead.
    console.warn('web billing: identify failed', err);
  }
}

/** Nothing to drop: the next `identify` switches the SDK to the next account,
    and until then there is no signed-in user to purchase for. Kept as a hook
    so sign-out has the same shape as on mobile. */
function forget(): void {
  /* intentionally empty — see above */
}

async function storeSaysEntitled(): Promise<boolean> {
  if (!instance) return false;
  try {
    const info = await instance.getCustomerInfo();
    return ENTITLEMENT_ID in info.entitlements.active;
  } catch {
    return false;
  }
}

/** The web SDK has no live customer-info listener, and does not need one: the
    only in-session change is a purchase made HERE, and the paywall grants
    optimistically the moment `purchase()` resolves. */
function onStoreEntitlementChange(): () => void {
  return () => {};
}

export const billing: BillingAdapter = { identify, forget, storeSaysEntitled, onStoreEntitlementChange };

/* ---------- what the paywall draws ---------- */

/** One purchasable plan, flattened so the paywall renders no SDK types. */
export interface WebOffer {
  id: string;
  /** `year` / `month`; null for a period the copy has no word for. */
  period: 'year' | 'month' | null;
  /** Localised, from the store — never hardcoded, for the same reason the iOS
      paywall reads `priceString`. */
  price: string;
  /** "$2.50" — the annual price spread over twelve months, so the two plans
      can be compared on one line. Null unless the period is a year. */
  perMonth: string | null;
  /** "2 weeks", or null when the product carries no free trial. */
  trial: string | null;
  pkg: Package;
}

function periodWord(period: Period | null | undefined): 'year' | 'month' | null {
  if (!period) return null;
  if (period.unit === 'year' && period.number === 1) return 'year';
  if (period.unit === 'month' && period.number === 1) return 'month';
  return null;
}

/** "2 weeks", "1 month", "14 days" — whatever the trial phase says. */
function trialWord(phase: PricingPhase | null | undefined): string | null {
  const p = phase?.period;
  if (!p || p.number <= 0) return null;
  return `${p.number} ${p.unit}${p.number === 1 ? '' : 's'}`;
}

/** `Price.amount` is in the currency's MINOR unit (2999 for $29.99), so the
    arithmetic runs on `amountMicros`, which is unambiguous. */
function perMonthOf(amountMicros: number, currency: string, period: 'year' | 'month' | null): string | null {
  if (period !== 'year') return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMicros / 1_000_000 / 12);
  } catch {
    return null;
  }
}

/** The current offering's packages, yearly first. Empty when the SDK is not
    configured, has no key, or the network is down — the paywall words each of
    those as "plans couldn't be loaded", which is honest for all three. */
export async function loadOffers(): Promise<WebOffer[]> {
  if (!instance) return [];
  try {
    const offerings = await instance.getOfferings();
    const packages = offerings.current?.availablePackages ?? [];
    const offers = packages.map((pkg): WebOffer => {
      const product = pkg.webBillingProduct;
      const period = periodWord(product.period);
      return {
        id: pkg.identifier,
        period,
        price: product.currentPrice.formattedPrice,
        perMonth: perMonthOf(product.currentPrice.amountMicros, product.currentPrice.currency, period),
        trial: trialWord(product.freeTrialPhase),
        pkg,
      };
    });
    const rank = (o: WebOffer) => (o.period === 'year' ? 0 : o.period === 'month' ? 1 : 2);
    return offers.sort((a, b) => rank(a) - rank(b));
  } catch (err) {
    console.warn('web billing: offerings failed', err);
    return [];
  }
}

export type PurchaseOutcome = { kind: 'purchased' } | { kind: 'cancelled' } | { kind: 'error'; message: string };

/** Run the checkout. The SDK mounts Stripe's payment sheet in a modal of its
    own; this resolves when it closes. `email` pre-fills the receipt address so
    the sheet does not ask for one the account already has. */
export async function purchase(offer: WebOffer, email: string | null | undefined): Promise<PurchaseOutcome> {
  if (!instance) return { kind: 'error', message: 'Billing is not available right now.' };
  try {
    await instance.purchase({
      rcPackage: offer.pkg,
      customerEmail: email ?? undefined,
      termsAndConditionsUrl: 'https://habishine.com/terms.html',
    });
    return { kind: 'purchased' };
  } catch (err) {
    if (err instanceof PurchasesError && err.errorCode === ErrorCode.UserCancelledError) {
      return { kind: 'cancelled' };
    }
    console.warn('web billing: purchase failed', err);
    return { kind: 'error', message: 'The payment didn’t go through. Nothing was charged — please try again.' };
  }
}

/** RevenueCat's hosted customer portal for this customer: change the card,
    cancel, see receipts. Null when there is no web subscription to manage —
    an App Store subscriber gets no portal here, and Settings says so. */
export async function managementUrl(): Promise<string | null> {
  if (!instance) return null;
  try {
    const info = await instance.getCustomerInfo();
    return info.managementURL ?? null;
  } catch {
    return null;
  }
}
